import { useState, useEffect, useCallback } from 'react';

interface NavPanelPosition {
  left: number;
  width: number;
  bottom: number | null; // null means stick to viewport bottom, number means offset from viewport bottom
}

export const useNavPanelPosition = (): NavPanelPosition => {
  const [position, setPosition] = useState<NavPanelPosition>({
    left: 0,
    width: 400,
    bottom: null,
  });

  const updatePosition = useCallback(() => {
    const navPanel = document.querySelector('[data-testid="edit-navigation"]');
    if (!navPanel) return;

    const rect = navPanel.getBoundingClientRect();

    // Check if we're near the bottom of the page (footer overlap detection)
    const documentHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrollTop = window.scrollY;
    const distanceFromBottom = documentHeight - (scrollTop + viewportHeight);

    // If we're within 165px of the bottom, start adjusting
    // This prevents overlap with footer content
    const footerBuffer = 165;
    const bottomOffset =
      distanceFromBottom < footerBuffer
        ? footerBuffer - distanceFromBottom
        : null;

    setPosition({
      left: rect.left,
      width: rect.width,
      bottom: bottomOffset,
    });
  }, []);

  useEffect(() => {
    updatePosition();

    // Update on scroll and resize
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);

    // Also observe the nav panel for size changes
    const navPanel = document.querySelector('[data-testid="edit-navigation"]');
    let resizeObserver: ResizeObserver | null = null;

    if (navPanel) {
      resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(navPanel);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      resizeObserver?.disconnect();
    };
  }, [updatePosition]);

  return position;
};
